import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EmptyState,
  Switch,
  DataTable,
  TopOfMindPanel,
  type DataTableColumn,
} from '@wundo/ui';
import {
  linkCohortDocument,
  listCohortDocuments,
  unlinkCohortDocument,
} from '@/lib/cohorts.ts';
import { listTrackDocuments } from '@/lib/tracks.ts';
import { listDocuments } from '@/lib/documents.ts';
import { useTopOfMind } from '@/lib/useTopOfMind.ts';
import type { Document } from '@/types/documents.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export interface CohortDocumentsTabProps {
  cohortId: string;
  /** The cohort's track, when it has one: what it inherits comes from there. */
  trackId: string | null;
  organisationId: string;
}

/**
 * Which of the organisation's documents this cohort teaches from.
 *
 * The whole library is listed and each row carries a switch, rather than a
 * separate "add document" screen: the question is always "is this one in or
 * out", and answering it is one click on the row you are already looking at.
 * A document the cohort's track already brings is on and locked — the track
 * owns that link, and unlinking it here would silently do nothing.
 */
export function CohortDocumentsTab({
  cohortId,
  trackId,
  organisationId,
}: CohortDocumentsTabProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [toggleError, setToggleError] = useState('');

  const linkedKey = ['cohorts', cohortId, 'documents'];

  const linked = useQuery({
    queryKey: linkedKey,
    queryFn: () => listCohortDocuments(cohortId),
  });

  const topOfMind = useTopOfMind({ type: 'cohort', id: cohortId }, setToggleError);

  const inherited = useQuery({
    queryKey: ['tracks', trackId, 'documents'],
    queryFn: () => listTrackDocuments(trackId!),
    enabled: trackId !== null,
  });

  const documents = useQuery({
    queryKey: ['documents', 'cohort-candidates', organisationId, page, query],
    queryFn: () =>
      listDocuments({
        organisationId,
        page,
        limit: PAGE_SIZE,
        query: query || undefined,
      }),
  });

  /* document_id → the pivot row's id, which is what an unlink needs. */
  const linkedById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of linked.data ?? []) map.set(row.document_id, row.id);
    return map;
  }, [linked.data]);

  const inheritedIds = useMemo(
    () => new Set((inherited.data ?? []).map((row) => row.document_id)),
    [inherited.data],
  );

  const toggle = useMutation({
    mutationFn: async (document: Document) => {
      const pivotId = linkedById.get(document.id);
      if (pivotId) await unlinkCohortDocument(pivotId);
      else await linkCohortDocument(cohortId, document.id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: linkedKey }),
        queryClient.invalidateQueries({ queryKey: topOfMind.queryKey }),
      ]);
    },
    onError: (failure, document) => {
      setToggleError(
        apiErrorMessage(failure, `Failed to update link for ${document.file_name}`),
      );
    },
  });

  const busyId = toggle.isPending ? toggle.variables?.id : undefined;

  const columns: DataTableColumn<Document>[] = [
    { key: 'file_name', header: 'File Name' },
    { key: 'file_size', header: 'Size', render: (row) => formatFileSize(row.file_size) },
    {
      key: 'created_at',
      header: 'Uploaded At',
      render: (row) => new Date(row.created_at).toLocaleString(),
    },
    {
      key: 'linked',
      header: 'Linked to Cohort',
      render: (row) => {
        const viaTrack = inheritedIds.has(row.id);
        return (
          <div className='flex items-center gap-2'>
            <Switch
              aria-label={`Link ${row.file_name} to this cohort`}
              checked={viaTrack || linkedById.has(row.id)}
              disabled={viaTrack || busyId === row.id}
              onCheckedChange={() => {
                setToggleError('');
                toggle.mutate(row);
              }}
            />
            {viaTrack && <span className='text-xs text-ink-muted'>via track</span>}
          </div>
        );
      },
    },
  ];

  return (
    <div className='flex flex-col gap-3'>
      {topOfMind.state && (
        <TopOfMindPanel
          state={topOfMind.state}
          holder='cohort'
          onRebuild={() => {
            setToggleError('');
            topOfMind.rebuild();
          }}
          rebuilding={topOfMind.rebuilding}
        />
      )}

      {toggleError !== '' && (
        <p
          role='alert'
          className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
        >
          {toggleError}
        </p>
      )}

      <DataTable<Document>
        label='Documents'
        columns={columns}
        rows={documents.data?.data ?? []}
        rowKey={(row) => row.id}
        loading={documents.isPending}
        error={documents.isError ? 'Failed to load documents' : undefined}
        emptyState={
          <EmptyState
            title='No documents'
            description='Upload documents for this organisation to link them to a cohort.'
          />
        }
        search={{
          value: query,
          onChange: (next) => {
            setQuery(next);
            setPage(1);
          },
          placeholder: 'Search documents...',
        }}
        pagination={{
          page: documents.data?.meta.currentPage ?? page,
          pageSize: PAGE_SIZE,
          total: documents.data?.meta.totalItems ?? 0,
        }}
        onPageChange={setPage}
      />
    </div>
  );
}
